import { useEffect, useState } from "react";
import { Button } from "@humansignal/ui";
import { cn } from "@afriannotate/datamanager/utils/bem";
import { CollectionSubmissions } from "./CollectionSubmissions";
import "./Label.prefix.css";

/** Open collection: the contributor's records on this prompt against the
 *  goal, and a way to stop. Shown only when the project keeps prompts open. */
export const CollectionBar = ({ SDK }) => {
  const [c, setC] = useState(null);
  const [panelOpen, setPanelOpen] = useState(false);
  useEffect(() => {
    // The editor wrapper (SDK.lsf) is created once labeling starts.
    let unsubscribe;
    const attach = () => {
      if (!SDK?.lsf?.subscribeRepeat) return false;
      unsubscribe = SDK.lsf.subscribeRepeat(setC);
      return true;
    };
    if (attach()) return () => unsubscribe?.();
    const timer = setInterval(() => attach() && clearInterval(timer), 200);
    return () => {
      clearInterval(timer);
      unsubscribe?.();
    };
  }, [SDK]);
  if (!c?.open) return null;
  const { mine = 0, goal, limit } = c;
  const unit = mine === 1 ? "record" : "records";
  const label = goal ? `${mine} of ${goal} records` : `${mine} ${unit}`;
  const pct = goal ? Math.min(100, Math.round((mine / goal) * 100)) : 0;
  const note = c.can_add === false ? (limit && mine >= limit ? "Limit reached" : "Prompt closed") : null;
  // "Your submissions" opens as a panel from the right, over the editor's
  // own side panels, so the prompt and the form stay in view.
  const panel = panelOpen ? (
    <aside
      className={cn("label-view").elem("submissions-panel").toClassName()}
      aria-label="Your submissions"
      onKeyDown={(e) => e.key === "Escape" && setPanelOpen(false)}
    >
      <div className={cn("label-view").elem("submissions-head").toClassName()}>
        <h2>Your submissions</h2>
        <Button look="string" size="small" onClick={() => setPanelOpen(false)} aria-label="Close your submissions">
          Close
        </Button>
      </div>
      <CollectionSubmissions lsf={SDK.lsf} onClose={() => setPanelOpen(false)} />
    </aside>
  ) : null;
  if (c.editing) {
    return (
      <div
        className={`${cn("label-view").elem("collection").toClassName()} ${cn("label-view").elem("mode-panel").toClassName()}`}
        role="status"
      >
        <div className={cn("label-view").elem("collection-text").toClassName()}>
          <strong>Editing a saved record</strong>
        </div>
        <Button look="outlined" size="small" onClick={() => SDK.lsf?.newOwn()}>
          New record
        </Button>
        {panel}
      </div>
    );
  }
  return (
    <div
      className={`${cn("label-view").elem("collection").toClassName()} ${cn("label-view").elem("mode-panel").toClassName()}`}
      role="status"
      aria-live="polite"
    >
      <div className={cn("label-view").elem("collection-text").toClassName()}>
        <strong>{label}</strong>
        {note ? <span className={cn("label-view").elem("collection-note").toClassName()}>{note}</span> : null}
      </div>
      {goal ? (
        <div
          className={cn("label-view").elem("collection-track").toClassName()}
          role="progressbar"
          aria-label="Records toward your goal"
          aria-valuemin={0}
          aria-valuemax={goal}
          aria-valuenow={Math.min(mine, goal)}
        >
          <div className={cn("label-view").elem("collection-fill").toClassName()} style={{ width: `${pct}%` }} />
        </div>
      ) : null}
      <span className={cn("label-view").elem("collection-actions").toClassName()}>
        <Button look="string" size="small" aria-expanded={panelOpen} onClick={() => setPanelOpen((v) => !v)}>
          Your submissions
        </Button>
        <Button look="outlined" size="small" onClick={() => SDK.lsf?.leaveRepeat()}>
          Done for now
        </Button>
      </span>
      {panel}
    </div>
  );
};
